import React, { useState, useRef } from "react";
import {
  Dialog,
  TextField,
  Box,
  IconButton,
  FormControlLabel,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axiosInstance from "../../../utils/config/config";
import {
  TELEHEALTH_MODAL_TITLE,
  TELEHEALTH_CONSENT_VERSION,
  TELEHEALTH_SIGN_BUTTON_TEXT,
  TELEHEALTH_CANCEL_BUTTON_TEXT,
  TELEHEALTH_SIGNING_TEXT,
  TELEHEALTH_SIGNATURE_LABEL,
  TELEHEALTH_SIGNATURE_PLACEHOLDER,
  TELEHEALTH_CHECKBOX_LABEL,
  TELEHEALTH_SUCCESS_MESSAGE,
  TELEHEALTH_ERROR_MESSAGE,
  TELEHEALTH_CONSENT_CONTENT,
  TELEHEALTH_LOCATION_LABEL,
  TELEHEALTH_LOCATION_PLACEHOLDER,
  TELEHEALTH_EMERGENCY_NAME_LABEL,
  TELEHEALTH_EMERGENCY_NAME_PLACEHOLDER,
  TELEHEALTH_EMERGENCY_PHONE_LABEL,
  TELEHEALTH_EMERGENCY_PHONE_PLACEHOLDER,
  TELEHEALTH_GUARDIAN_SECTION_TITLE,
  TELEHEALTH_GUARDIAN_SECTION_NOTICE,
  TELEHEALTH_GUARDIAN_NAME_LABEL,
  TELEHEALTH_GUARDIAN_NAME_PLACEHOLDER,
  TELEHEALTH_GUARDIAN_SIGNATURE_LABEL,
  TELEHEALTH_GUARDIAN_SIGNATURE_PLACEHOLDER,
  TELEHEALTH_GUARDIAN_RELATIONSHIP_LABEL,
  TELEHEALTH_GUARDIAN_EMAIL_LABEL,
  TELEHEALTH_GUARDIAN_EMAIL_PLACEHOLDER,
  TELEHEALTH_GUARDIAN_RELATIONSHIP_OPTIONS,
} from "./TelehealthConsentModal.constants";
import {
  StyledDialogTitle,
  StyledTitleText,
  StyledDialogContent,
  ConsentScrollBox,
  ConsentSectionHeading,
  ConsentSectionSubtitle,
  ConsentText,
  ConsentListItem,
  SignatureSection,
  SignatureSectionTitle,
  StyledCheckbox,
  CheckboxLabel,
  DateDisplay,
  ActionBox,
  SignButton,
  CancelButton,
  SuccessBanner,
  SuccessText,
  GuardianSection,
  GuardianSectionTitle,
  GuardianNotice,
  GuardianNoticeText,
} from "./TelehealthConsentModal.styles";

/** Simple email format check (mirrors backend validation). */
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/**
 * Signature / name field validation rules.
 * - Max length 255 matches backend DB cap (Sequelize STRING default = VARCHAR(255)).
 * - Allowed: letters (including accented À-ÿ), spaces, hyphens, apostrophes, periods.
 */
const SIGNATURE_MAX_LENGTH = 255;
const SIGNATURE_ALLOWED_REGEX = /^[A-Za-zÀ-ÿ\s'.-]*$/;
const SIGNATURE_INVALID_CHARS = /[^A-Za-zÀ-ÿ\s'.-]/g;
const SIGNATURE_INVALID_CHARS_MSG =
  "Only letters, spaces, hyphens, apostrophes, and periods are allowed";
const SIGNATURE_MAX_LENGTH_MSG = `Maximum ${SIGNATURE_MAX_LENGTH} characters allowed`;
const SIGNATURE_MIN_LENGTH_MSG = "Please enter your full legal name";

/** Generic STRING-length cap for free-text fields (matches Sequelize default). */
const GENERIC_TEXT_MAX_LENGTH = 255;

/** Strips disallowed characters and caps length. */
const sanitizeSignature = (value) =>
  value.replace(SIGNATURE_INVALID_CHARS, "").slice(0, SIGNATURE_MAX_LENGTH);

/** Returns an error message for the given (already-sanitized) value, or "" if valid. */
const getSignatureError = (value) => {
  if (!value) return "";
  if (!SIGNATURE_ALLOWED_REGEX.test(value)) return SIGNATURE_INVALID_CHARS_MSG;
  if (value.length > SIGNATURE_MAX_LENGTH) return SIGNATURE_MAX_LENGTH_MSG;
  const trimmed = value.trim();
  if (trimmed.length > 0 && trimmed.length < 2) return SIGNATURE_MIN_LENGTH_MSG;
  return "";
};

const INITIAL_GUARDIAN_STATE = {
  name: "",
  signature: "",
  relationship: "",
  email: "",
};

const INITIAL_OPTIONAL_FIELDS = {
  clientLocationState: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
};

const TelehealthConsentModal = ({
  open,
  onClose,
  onSigned,
  isMinor = false,
}) => {
  const [signedName, setSignedName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const successTimerRef = useRef(null);

  // Optional client-attested fields
  const [optionalFields, setOptionalFields] = useState(INITIAL_OPTIONAL_FIELDS);
  const updateOptional = (field, value) =>
    setOptionalFields((prev) => ({
      ...prev,
      [field]: value.slice(0, GENERIC_TEXT_MAX_LENGTH),
    }));

  // Per-field validation errors for the signature / name fields
  const [signedNameError, setSignedNameError] = useState("");
  const [guardianNameError, setGuardianNameError] = useState("");
  const [guardianSignatureError, setGuardianSignatureError] = useState("");

  // Parent / Legal Guardian fields (only used when isMinor is true)
  const [guardian, setGuardian] = useState(INITIAL_GUARDIAN_STATE);

  const updateGuardian = (field, value) =>
    setGuardian((prev) => ({ ...prev, [field]: value }));

  /**
   * Sanitize a raw input value and compute the appropriate user-facing error.
   * If the raw value contained disallowed characters or exceeded the max length,
   * we flash a specific error message so the user understands why the field
   * content changed.
   */
  const processSignatureInput = (raw) => {
    const clean = sanitizeSignature(raw);
    let errMsg = "";
    if (raw.length > SIGNATURE_MAX_LENGTH) {
      errMsg = SIGNATURE_MAX_LENGTH_MSG;
    } else if (raw !== clean) {
      errMsg = SIGNATURE_INVALID_CHARS_MSG;
    } else {
      errMsg = getSignatureError(clean);
    }
    return { clean, errMsg };
  };

  const handleSignedNameChange = (e) => {
    const { clean, errMsg } = processSignatureInput(e.target.value);
    setSignedName(clean);
    setSignedNameError(errMsg);
  };

  const handleGuardianNameChange = (e) => {
    const { clean, errMsg } = processSignatureInput(e.target.value);
    updateGuardian("name", clean);
    setGuardianNameError(errMsg);
  };

  const handleGuardianSignatureChange = (e) => {
    const { clean, errMsg } = processSignatureInput(e.target.value);
    updateGuardian("signature", clean);
    setGuardianSignatureError(errMsg);
  };

  // --- Validation ---
  const clientFieldsValid =
    signedName.trim().length >= 2 &&
    !getSignatureError(signedName) &&
    agreed;

  const guardianFieldsValid = isMinor
    ? guardian.name.trim().length >= 2 &&
      guardian.signature.trim().length >= 2 &&
      !getSignatureError(guardian.name) &&
      !getSignatureError(guardian.signature) &&
      guardian.relationship.trim().length > 0 &&
      guardian.email.trim().length > 0 &&
      isValidEmail(guardian.email.trim())
    : true;

  const canSign = clientFieldsValid && guardianFieldsValid && !signing;

  // --- Handlers ---
  const resetState = () => {
    setSignedName("");
    setAgreed(false);
    setGuardian(INITIAL_GUARDIAN_STATE);
    setOptionalFields(INITIAL_OPTIONAL_FIELDS);
    setSignedNameError("");
    setGuardianNameError("");
    setGuardianSignatureError("");
    setError(null);
    setSuccess(false);
  };

  const handleSign = async () => {
    try {
      setSigning(true);
      setError(null);

      const payload = {
        signedName: signedName.trim(),
        consentVersion: TELEHEALTH_CONSENT_VERSION,
      };

      // Optional fields — only include when the client filled them in
      const loc = optionalFields.clientLocationState.trim();
      const ecn = optionalFields.emergencyContactName.trim();
      const ecp = optionalFields.emergencyContactPhone.trim();
      if (loc) payload.clientLocationState = loc;
      if (ecn) payload.emergencyContactName = ecn;
      if (ecp) payload.emergencyContactPhone = ecp;

      // Include guardian fields for minor clients
      if (isMinor) {
        payload.guardianName = guardian.name.trim();
        payload.guardianSignature = guardian.signature.trim();
        payload.guardianRelationship = guardian.relationship.trim();
        payload.guardianEmail = guardian.email.trim();
      }

      const response = await axiosInstance.post(
        "telehealth-consents/sign",
        payload,
      );

      if (response.data.success) {
        setSigning(false);
        setSuccess(true);
        successTimerRef.current = setTimeout(() => {
          if (onSigned) onSigned(response.data.consent);
          resetState();
          onClose();
        }, 1500);
      } else {
        setSigning(false);
        setError(response.data.message || TELEHEALTH_ERROR_MESSAGE);
      }
    } catch (err) {
      setSigning(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          TELEHEALTH_ERROR_MESSAGE,
      );
    }
  };

  const handleClose = () => {
    if (signing) return;
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    resetState();
    onClose();
  };

  const todayFormatted = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  /** Shared sx for all text fields in the modal. */
  const textFieldSx = {
    mb: 2,
    "& .MuiOutlinedInput-root": {
      fontFamily: "'Gotham', sans-serif",
      borderRadius: "10px",
    },
    "& .MuiInputLabel-root": {
      fontFamily: "'Gotham', sans-serif",
    },
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <StyledTitleText>{TELEHEALTH_MODAL_TITLE}</StyledTitleText>
        <IconButton
          onClick={handleClose}
          disabled={signing}
          sx={{ color: "inherit" }}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {success && (
          <SuccessBanner>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <CheckCircleIcon sx={{ color: "#2E7D32" }} />
              <SuccessText>{TELEHEALTH_SUCCESS_MESSAGE}</SuccessText>
            </Box>
          </SuccessBanner>
        )}

        {/* Consent Content */}
        <ConsentScrollBox>
          <ConsentSectionHeading
            sx={{ fontSize: "1.15rem", marginBottom: 2 }}
          >
            {TELEHEALTH_CONSENT_CONTENT.title}
          </ConsentSectionHeading>
          <ConsentText sx={{ fontStyle: "italic", marginBottom: 2 }}>
            Effective: {TELEHEALTH_CONSENT_CONTENT.effectiveDate}
          </ConsentText>

          {TELEHEALTH_CONSENT_CONTENT.sections.map((section, index) => (
            <Box key={index}>
              <ConsentSectionHeading>{section.heading}</ConsentSectionHeading>
              {section.subtitle && (
                <ConsentSectionSubtitle>
                  {section.subtitle}
                </ConsentSectionSubtitle>
              )}
              {section.content && (
                <ConsentText>{section.content}</ConsentText>
              )}
              {section.items && (
                <ul style={{ paddingLeft: 24, margin: "8px 0" }}>
                  {section.items.map((item, i) => (
                    <ConsentListItem key={i}>{item}</ConsentListItem>
                  ))}
                </ul>
              )}
            </Box>
          ))}
        </ConsentScrollBox>

        {/* Client Digital Signature Section */}
        <SignatureSection>
          <SignatureSectionTitle>Digital Signature</SignatureSectionTitle>

          <TextField
            label={TELEHEALTH_SIGNATURE_LABEL}
            placeholder={TELEHEALTH_SIGNATURE_PLACEHOLDER}
            value={signedName}
            onChange={handleSignedNameChange}
            fullWidth
            variant="outlined"
            disabled={signing || success}
            inputProps={{ maxLength: SIGNATURE_MAX_LENGTH }}
            error={Boolean(signedNameError)}
            helperText={signedNameError}
            sx={textFieldSx}
          />

          {/* Optional but recommended attestation fields */}
          <TextField
            label={TELEHEALTH_LOCATION_LABEL}
            placeholder={TELEHEALTH_LOCATION_PLACEHOLDER}
            value={optionalFields.clientLocationState}
            onChange={(e) =>
              updateOptional("clientLocationState", e.target.value)
            }
            fullWidth
            variant="outlined"
            disabled={signing || success}
            inputProps={{ maxLength: GENERIC_TEXT_MAX_LENGTH }}
            sx={textFieldSx}
          />

          <TextField
            label={TELEHEALTH_EMERGENCY_NAME_LABEL}
            placeholder={TELEHEALTH_EMERGENCY_NAME_PLACEHOLDER}
            value={optionalFields.emergencyContactName}
            onChange={(e) =>
              updateOptional("emergencyContactName", e.target.value)
            }
            fullWidth
            variant="outlined"
            disabled={signing || success}
            inputProps={{ maxLength: GENERIC_TEXT_MAX_LENGTH }}
            sx={textFieldSx}
          />

          <TextField
            label={TELEHEALTH_EMERGENCY_PHONE_LABEL}
            placeholder={TELEHEALTH_EMERGENCY_PHONE_PLACEHOLDER}
            value={optionalFields.emergencyContactPhone}
            onChange={(e) =>
              updateOptional("emergencyContactPhone", e.target.value)
            }
            fullWidth
            variant="outlined"
            disabled={signing || success}
            inputProps={{ maxLength: GENERIC_TEXT_MAX_LENGTH }}
            sx={textFieldSx}
          />

          <FormControlLabel
            control={
              <StyledCheckbox
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={signing || success}
              />
            }
            label={<CheckboxLabel>{TELEHEALTH_CHECKBOX_LABEL}</CheckboxLabel>}
            sx={{ alignItems: "flex-start", mb: 1 }}
          />

          <DateDisplay>Date: {todayFormatted}</DateDisplay>
        </SignatureSection>

        {/* Parent / Legal Guardian Section (only for minors 14-17) */}
        {isMinor && (
          <GuardianSection>
            <GuardianSectionTitle>
              {TELEHEALTH_GUARDIAN_SECTION_TITLE}
            </GuardianSectionTitle>

            <GuardianNotice>
              <GuardianNoticeText>
                {TELEHEALTH_GUARDIAN_SECTION_NOTICE}
              </GuardianNoticeText>
            </GuardianNotice>

            <TextField
              label={TELEHEALTH_GUARDIAN_NAME_LABEL}
              placeholder={TELEHEALTH_GUARDIAN_NAME_PLACEHOLDER}
              value={guardian.name}
              onChange={handleGuardianNameChange}
              fullWidth
              variant="outlined"
              disabled={signing || success}
              inputProps={{ maxLength: SIGNATURE_MAX_LENGTH }}
              error={Boolean(guardianNameError)}
              helperText={guardianNameError}
              sx={textFieldSx}
            />

            <TextField
              label={TELEHEALTH_GUARDIAN_SIGNATURE_LABEL}
              placeholder={TELEHEALTH_GUARDIAN_SIGNATURE_PLACEHOLDER}
              value={guardian.signature}
              onChange={handleGuardianSignatureChange}
              fullWidth
              variant="outlined"
              disabled={signing || success}
              inputProps={{ maxLength: SIGNATURE_MAX_LENGTH }}
              error={Boolean(guardianSignatureError)}
              helperText={guardianSignatureError}
              sx={textFieldSx}
            />

            <TextField
              select
              label={TELEHEALTH_GUARDIAN_RELATIONSHIP_LABEL}
              value={guardian.relationship}
              onChange={(e) => updateGuardian("relationship", e.target.value)}
              fullWidth
              variant="outlined"
              disabled={signing || success}
              sx={textFieldSx}
            >
              {TELEHEALTH_GUARDIAN_RELATIONSHIP_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label={TELEHEALTH_GUARDIAN_EMAIL_LABEL}
              placeholder={TELEHEALTH_GUARDIAN_EMAIL_PLACEHOLDER}
              type="email"
              value={guardian.email}
              onChange={(e) => updateGuardian("email", e.target.value)}
              fullWidth
              variant="outlined"
              disabled={signing || success}
              error={
                guardian.email.trim().length > 0 &&
                !isValidEmail(guardian.email.trim())
              }
              helperText={
                guardian.email.trim().length > 0 &&
                !isValidEmail(guardian.email.trim())
                  ? "Please enter a valid email address"
                  : ""
              }
              sx={textFieldSx}
            />

            <DateDisplay>Date: {todayFormatted}</DateDisplay>
          </GuardianSection>
        )}

        {/* Error display */}
        {error && (
          <ConsentText
            sx={{ color: "#D32F2F", fontWeight: 600, mt: 1.5 }}
          >
            {error}
          </ConsentText>
        )}

        {/* Actions */}
        <ActionBox>
          <CancelButton
            text={TELEHEALTH_CANCEL_BUTTON_TEXT}
            noIcon
            onClick={handleClose}
            disabled={signing}
          />
          <SignButton
            text={signing ? TELEHEALTH_SIGNING_TEXT : TELEHEALTH_SIGN_BUTTON_TEXT}
            noIcon
            onClick={handleSign}
            disabled={!canSign || success}
          />
        </ActionBox>
      </StyledDialogContent>
    </Dialog>
  );
};

export default TelehealthConsentModal;
